import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useExpanded, useFlexLayout, useTable } from 'react-table';

import { ReactTableWrapper } from 'ui-components/components/react-table-wrapper/react-table-wrapper';

import { AdditionalItemsRowGroupHeaderContent } from './additional-items/additional-items-row-group-header-content';
import { ItemsTableSubRows } from './items-table-sub-rows';
import style from './style.module.less';
import { emptyArray } from 'ui-components/default-props/empty';
import { useEnableAddShipmentItems } from '../../../../feature-flags/hooks/use-enable-add-shipment-items';
import { useEnableReship } from '../../../../feature-flags/hooks/use-enable-reship';

const getRowClassName = (row) => {
  return row?.original?.itemInventoryClassName;
};

const ItemsTable = ({ columns, items, hiddenColumns = emptyArray, emptyRow }) => {
  const isReshipEnabled = useEnableReship();
  const isAddShipmentItemsEnabled = useEnableAddShipmentItems();
  const { getTableProps, headerGroups, getTableBodyProps, rows, prepareRow, setHiddenColumns, toggleAllRowsExpanded } = useTable(
    {
      columns,
      data: items,
      initialState: {
        hiddenColumns,
      },
      autoResetExpanded: false,
    },
    useFlexLayout,
    useExpanded,
  );

  // Initialize all the rows to be expanded
  useLayoutEffect(toggleAllRowsExpanded, [toggleAllRowsExpanded]);

  useLayoutEffect(() => {
    setHiddenColumns(hiddenColumns);
  }, [setHiddenColumns, hiddenColumns]);

  const rowGroups = useMemo(() => {
    if (!rows.length) {
      return [];
    }

    if (!(isReshipEnabled || isAddShipmentItemsEnabled)) {
      return [{ rows }];
    }

    const salesOrderItemRows = [];
    const additionalItemRows = [];
    const itemRowGroups = [];

    rows.forEach((item) => {
      if (item.original.salesOrderItemId) {
        salesOrderItemRows.push(item);
      } else {
        additionalItemRows.push(item);
      }
    });

    if (salesOrderItemRows.length) {
      itemRowGroups.push({
        rows: salesOrderItemRows,
      });
    }

    if (additionalItemRows.length) {
      itemRowGroups.push({
        header: <AdditionalItemsRowGroupHeaderContent />,
        rows: additionalItemRows,
      });
    }

    return itemRowGroups;
  }, [isAddShipmentItemsEnabled, isReshipEnabled, rows]);

  return (
    <ReactTableWrapper
      className={style['items-table']}
      emptyRow={emptyRow}
      getBodyRowClassName={getRowClassName}
      getTableBodyProps={getTableBodyProps}
      getTableProps={getTableProps}
      headerGroups={headerGroups}
      headerRowClassName={style['header-row']}
      prepareRow={prepareRow}
      renderSubRow={useCallback(
        (row) => (
          <ItemsTableSubRows row={row} />
        ),
        [],
      )}
      rowGroups={rowGroups}
    />
  );
};

ItemsTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      header: PropTypes.node,
      accessor: PropTypes.string,
      cell: PropTypes.elementType,
      width: PropTypes.number,
    }),
  ).isRequired,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  hiddenColumns: PropTypes.arrayOf(PropTypes.string),
  emptyRow: PropTypes.node,
};

export default ItemsTable;
